import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessageSquareText, Loader2, Copy, Check, Lock, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../lib/i18n";
import { useSeo } from "../../lib/useSeo";
import { PageHeader } from "../../components/PageHeader";
import { PremiumPill } from "../../components/PremiumPill";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";

const Reactiecoach = () => {
  const { t } = useT();
  useSeo({ title: t("tools.coach.seoTitle"), description: t("tools.coach.seoDesc"), path: "/reactiecoach" });
  const { user } = useAuth();
  const [search, setSearch] = useSearchParams();
  const [listings, setListings] = useState(null);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(search.get("listing") || "");
  const [tone, setTone] = useState("uitgebreid");
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const isPremium = !!user?.is_premium;

  useEffect(() => {
    if (!isPremium) return;
    api("/listings?sort=match_desc")
      .then((r) => setListings(r.listings || []))
      .catch(() => setListings([]));
  }, [isPremium]);

  const filtered = useMemo(() => {
    const items = listings || [];
    if (!q.trim()) return items.slice(0, 30);
    const needle = q.toLowerCase();
    return items
      .filter((l) =>
        (l.title || "").toLowerCase().includes(needle) ||
        (l.city || "").toLowerCase().includes(needle) ||
        (l.street || "").toLowerCase().includes(needle)
      )
      .slice(0, 30);
  }, [listings, q]);

  const selected = listings?.find((l) => l.id === selectedId);

  const generate = async () => {
    if (!selectedId) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const res = await api("/response-coach", {
        method: "POST",
        body: JSON.stringify({ listing_id: selectedId, tone, extra_notes: extra || null }),
      });
      setResult(res);
    } catch (e) {
      setError(e.message || t("tools.coach.error"));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!result?.letter) return;
    try {
      await navigator.clipboard.writeText(result.letter);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // clipboard not available — fail silently
    }
  };

  if (!isPremium) {
    return (
      <div>
        <PageHeader icon={MessageSquareText} title={t("tools.coach.title")} subtitle={t("tools.coach.subtitle")} />
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
          <div className="rounded-3xl border border-amber-300/60 bg-gradient-to-br from-amber-50/40 via-white to-white p-10 text-center shadow-sm">
            <Lock className="mx-auto text-amber-500" size={36} />
            <h2 className="mt-4 font-display text-2xl font-extrabold text-navy">{t("tools.locked.title")}</h2>
            <p className="mx-auto mt-2 max-w-lg text-slate-600">{t("tools.locked.sub")}</p>
            <Button
              asChild
              size="lg"
              className="mt-6 rounded-full bg-navy text-amber-300 hover:bg-navy/90"
              data-testid="coach-unlock-cta"
            >
              <Link to="/prijzen">
                <Sparkles size={16} className="mr-2" /> {t("tools.locked.cta")}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="coach-page">
      <PageHeader
        icon={MessageSquareText}
        title={t("tools.coach.title")}
        subtitle={t("tools.coach.subtitle")}
        right={<PremiumPill active labelActive={t("premium.pill.active")} />}
      />

      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-2">
        {/* LEFT: config */}
        <div className="rounded-3xl border border-amber-300/50 bg-gradient-to-br from-amber-50/30 via-white to-white p-6 shadow-sm">
          <label className="font-display text-sm font-bold text-navy">{t("tools.coach.pickListing")}</label>
          <Input
            data-testid="coach-search-input"
            className="mt-2 rounded-xl"
            placeholder={t("tools.coach.searchPh")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="mt-3 max-h-[280px] overflow-y-auto rounded-2xl border border-border">
            {listings === null ? (
              <div className="p-6 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">{t("tools.coach.noneFound")}</div>
            ) : (
              filtered.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  data-testid={`coach-listing-${l.id}`}
                  onClick={() => { setSelectedId(l.id); setSearch({ listing: l.id }); }}
                  className={`flex w-full items-center gap-3 border-b border-border p-3 text-left last:border-b-0 hover:bg-slate-50 ${selectedId === l.id ? "bg-amber-50/60" : ""}`}
                >
                  <div className="h-12 w-16 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                    {l.image && <img src={l.image} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold text-navy">{l.title}</div>
                    <div className="truncate text-xs text-slate-500">{l.city} · €{l.price}/mnd</div>
                  </div>
                  {typeof l.match_score === "number" && (
                    <span className="rounded-full bg-brandblue-light px-2 py-0.5 text-xs font-bold text-brandblue">{l.match_score}%</span>
                  )}
                </button>
              ))
            )}
          </div>

          <div className="mt-5 flex gap-2">
            <button
              type="button"
              data-testid="coach-tone-kort"
              onClick={() => setTone("kort")}
              className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition ${tone === "kort" ? "bg-navy text-amber-300" : "bg-slate-100 text-slate-600"}`}
            >
              {t("tools.coach.toneShort")}
            </button>
            <button
              type="button"
              data-testid="coach-tone-lang"
              onClick={() => setTone("uitgebreid")}
              className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition ${tone === "uitgebreid" ? "bg-navy text-amber-300" : "bg-slate-100 text-slate-600"}`}
            >
              {t("tools.coach.toneLong")}
            </button>
          </div>

          <label className="mt-5 block font-display text-sm font-bold text-navy">{t("tools.coach.extraLabel")}</label>
          <Textarea
            data-testid="coach-extra-input"
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            rows={3}
            className="mt-2 rounded-xl"
            placeholder={t("tools.coach.extraPh")}
          />

          <Button
            data-testid="coach-generate-btn"
            disabled={!selectedId || busy}
            onClick={generate}
            className="mt-5 w-full rounded-full bg-navy text-amber-300 hover:bg-navy/90"
            size="lg"
          >
            {busy ? (
              <><Loader2 className="mr-2 animate-spin" size={16} /> {t("tools.coach.generating")}</>
            ) : (
              <><Sparkles size={16} className="mr-2" /> {t("tools.coach.generate")}</>
            )}
          </Button>
          {error && <p data-testid="coach-error" className="mt-3 text-sm text-danger">{error}</p>}
        </div>

        {/* RIGHT: result */}
        <div className="rounded-3xl border border-border bg-white p-6 shadow-sm">
          {!result ? (
            <div className="flex h-full min-h-[300px] flex-col items-center justify-center text-center text-slate-400">
              <MessageSquareText size={40} />
              <p className="mt-4 text-sm">
                {selected ? t("tools.coach.readyHint", { title: selected.title }) : t("tools.coach.emptyHint")}
              </p>
            </div>
          ) : (
            <div data-testid="coach-result">
              <div className="text-xs font-semibold uppercase tracking-widest text-amber-600">{t("tools.coach.result.subject")}</div>
              <h3 className="mt-1 font-display text-lg font-extrabold text-navy">{result.subject}</h3>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">{t("tools.coach.result.letter")}</span>
                <button
                  data-testid="coach-copy-btn"
                  onClick={copy}
                  className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100"
                >
                  {copied ? (
                    <><Check size={12} /> {t("tools.coach.copied")}</>
                  ) : (
                    <><Copy size={12} /> {t("tools.coach.copy")}</>
                  )}
                </button>
              </div>
              <pre className="mt-2 whitespace-pre-wrap rounded-2xl bg-slate-50 p-4 font-sans text-sm text-navy">{result.letter}</pre>
              {result.tips?.length > 0 && (
                <>
                  <div className="mt-5 text-xs font-semibold uppercase tracking-widest text-slate-400">{t("tools.coach.result.tips")}</div>
                  <ul className="mt-2 space-y-2">
                    {result.tips.map((tip, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <Sparkles size={14} className="mt-0.5 shrink-0 text-amber-500" /> <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reactiecoach;
